/**
 * Where one Timeline item's thumbs up/down lands on the backend.
 *
 * PATCH /conversations/sessions/:id/entry-feedback picks the array from
 * `entryType` + `category` (landmine → landmineDetections, competitor →
 * competitorDetections, 'FAQ' → detectedFAQs, 'New Inquiry'/'NEW_INQUIRY' →
 * newInquiries, anything else → detectedObjections) and matches the item by
 * `detected_at` (landmine/competitor) or `timestamp` (the rest). A wrong
 * category or a missing timestamp writes nothing, so those items get no control.
 */
export type EntryFeedbackSource = 'objection' | 'faq' | 'inquiry' | 'landmine' | 'competitor';

export interface EntryFeedbackTarget {
  entryType: 'conversation' | 'landmine' | 'competitor';
  entryTimestamp: string;
  category?: string;
}

interface FeedbackItem {
  category?: string;
  timestamp?: string;
  detected_at?: string;
}

// Categories the backend routes away from detectedObjections.
const NON_OBJECTION_CATEGORIES = new Set(['FAQ', 'New Inquiry', 'NEW_INQUIRY']);

export const entryFeedbackTarget = (
  source: EntryFeedbackSource,
  item: FeedbackItem | null | undefined,
): EntryFeedbackTarget | null => {
  if (!item) return null;

  if (source === 'landmine' || source === 'competitor') {
    return item.detected_at ? { entryType: source, entryTimestamp: item.detected_at } : null;
  }

  if (!item.timestamp) return null;

  if (source === 'faq') {
    return { entryType: 'conversation', entryTimestamp: item.timestamp, category: 'FAQ' };
  }
  if (source === 'inquiry') {
    return { entryType: 'conversation', entryTimestamp: item.timestamp, category: 'New Inquiry' };
  }
  const category = item.category && !NON_OBJECTION_CATEGORIES.has(item.category) ? item.category : 'Objection';
  return { entryType: 'conversation', entryTimestamp: item.timestamp, category };
};
